import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { localInventoryAPI, subWarehousesAPI } from '@/lib/api';
import { formatDateShort, formatTime, getNow } from '@/lib/utils';
import { 
  ClipboardList, Package, Search, Save, 
  FileDown, History, RefreshCw, CheckCircle, Calendar,
  Clock, MapPin, AlertCircle, Filter, Warehouse, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const InventaristaPage = () => {
  const [location, setLocation] = useState(null);
  const [items, setItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [subWarehouseFilter, setSubWarehouseFilter] = useState('all');
  const [subWarehouses, setSubWarehouses] = useState([]);
  const [notes, setNotes] = useState('');
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory' or 'history'
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewInventoryDialog, setViewInventoryDialog] = useState({ open: false, inventory: null });

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [locationRes, itemsRes, statsRes, subWarehousesRes] = await Promise.all([
        localInventoryAPI.getMyLocation(),
        localInventoryAPI.getItems(),
        localInventoryAPI.getStats(),
        subWarehousesAPI.getAll(null, true) // Solo sub-bodegas activas
      ]);
      
      setLocation(locationRes.data);
      setItems(itemsRes.data);
      setStats(statsRes.data);
      setSubWarehouses(subWarehousesRes.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await localInventoryAPI.getHistory({ limit: 50 });
      setHistory(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (activeTab === 'history' && history.length === 0) {
      loadHistory();
    }
  }, [activeTab, history.length, loadHistory]);

  // Obtener lista de proveedores únicos
  const uniqueSuppliers = useMemo(() => {
    const suppliers = new Set();
    items.forEach(item => {
      if (item.supplier) {
        suppliers.add(item.supplier);
      }
    });
    return Array.from(suppliers).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    let filtered = items;
    
    // Filtrar por sub-bodega
    if (subWarehouseFilter && subWarehouseFilter !== 'all') {
      filtered = filtered.filter(item => item.sub_warehouse_id === subWarehouseFilter);
    }
    
    // Filtrar por proveedor
    if (supplierFilter && supplierFilter !== 'all') {
      filtered = filtered.filter(item => item.supplier === supplierFilter);
    }
    
    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(term) ||
        item.code.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  }, [items, searchTerm, supplierFilter, subWarehouseFilter]);

  // Obtener la sub-bodega seleccionada para mostrar su nombre
  const selectedSubWarehouse = useMemo(() => {
    if (subWarehouseFilter === 'all') return null;
    return subWarehouses.find(sw => sw.id === subWarehouseFilter);
  }, [subWarehouseFilter, subWarehouses]);

  const handleQuantityChange = (itemId, value) => {
    const numValue = parseFloat(value) || 0;
    setInventoryItems(prev => ({
      ...prev,
      [itemId]: numValue
    }));
  };

  const itemsWithQuantity = useMemo(() => {
    return Object.entries(inventoryItems).filter(([_, qty]) => qty > 0);
  }, [inventoryItems]);

  const totalQuantity = useMemo(() => {
    return itemsWithQuantity.reduce((sum, [_, qty]) => sum + qty, 0);
  }, [itemsWithQuantity]);

  const saveInventory = async () => {
    if (itemsWithQuantity.length === 0) {
      toast.error('Debes ingresar al menos un producto con cantidad');
      return;
    }

    setSaving(true);
    try {
      const inventoryData = {
        location_id: location.id,
        sub_warehouse_id: subWarehouseFilter !== 'all' ? subWarehouseFilter : null,
        items: itemsWithQuantity.map(([itemId, qty]) => {
          const item = items.find(i => i.id === itemId);
          return {
            item_id: itemId,
            item_code: item?.code || '',
            item_name: item?.name || '',
            unit: item?.unit || 'unidad',
            quantity: qty
          };
        }),
        notes: notes || null
      };

      await localInventoryAPI.create(inventoryData);
      toast.success('Inventario guardado correctamente');
      
      // Limpiar formulario
      setInventoryItems({});
      setNotes('');
      
      // Recargar stats
      const statsRes = await localInventoryAPI.getStats();
      setStats(statsRes.data);
      
      // Recargar historial si está activo
      if (activeTab === 'history') {
        loadHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar inventario');
    } finally {
      setSaving(false);
    }
  };

  const exportToPDF = (inventory = null) => {
    // Si no se pasa inventario, usar el actual
    const itemsToExport = inventory 
      ? inventory.items 
      : itemsWithQuantity.map(([itemId, qty]) => {
          const item = items.find(i => i.id === itemId);
          return {
            item_code: item?.code || '',
            item_name: item?.name || '',
            unit: item?.unit || 'unidad',
            quantity: qty
          };
        });

    if (itemsToExport.length === 0) {
      toast.error('No hay productos para exportar');
      return;
    }

    const doc = new jsPDF();
    
    // Formatear fecha como DD/MM/YYYY para mejor legibilidad (zona horaria Chile)
    const formatDatePDF = (dateStr) => {
      if (!dateStr) return formatDateShort(new Date());
      // Si viene en formato YYYY-MM-DD, convertir a DD/MM/YYYY
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateStr;
    };
    
    const dateStr = formatDatePDF(inventory?.inventory_date);
    const timeStr = inventory?.inventory_time || formatTime(new Date());
    const locationName = inventory?.location_name || location?.name || 'Local';

    // Título
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('INVENTARIO DE LOCAL', 105, 20, { align: 'center' });

    // Línea divisoria
    doc.setLineWidth(0.5);
    doc.line(20, 25, 190, 25);

    // Info del inventario
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Local: ${locationName}`, 20, 35);
    doc.text(`Fecha: ${dateStr}`, 120, 35);
    doc.text(`Hora: ${timeStr}`, 120, 42);

    // Tabla de productos
    const tableData = itemsToExport.map((item, index) => [
      index + 1,
      item.item_code,
      item.item_name,
      item.quantity,
      item.unit
    ]);

    doc.autoTable({
      startY: 52,
      head: [['#', 'Código', 'Producto', 'Cantidad', 'Unidad']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [51, 51, 51], textColor: 255 },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 30 },
        2: { cellWidth: 80 },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 25 }
      }
    });

    // Total
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFont('helvetica', 'bold');
    doc.text(`Total productos: ${itemsToExport.length}`, 20, finalY);
    doc.text(`Total unidades: ${itemsToExport.reduce((sum, i) => sum + i.quantity, 0)}`, 100, finalY);

    // Notas si hay
    const inventoryNotes = inventory?.notes || notes;
    if (inventoryNotes) {
      doc.setFont('helvetica', 'normal');
      doc.text(`Notas: ${inventoryNotes}`, 20, finalY + 10);
    }

    // Pie de página
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(`Generado el ${getNow()} - TUPU Stock`, 105, 285, { align: 'center' });

    // Descargar
    const fileName = `inventario_${locationName.replace(/\s+/g, '_')}_${dateStr.replace(/\//g, '')}.pdf`;
    doc.save(fileName);
    toast.success('PDF generado correctamente');
  };

  const viewInventoryDetails = async (inventoryId) => {
    try {
      const response = await localInventoryAPI.getById(inventoryId);
      setViewInventoryDialog({ open: true, inventory: response.data });
    } catch (error) {
      toast.error('Error al cargar inventario');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="inventarista-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList className="w-8 h-8 text-primary" />
          <h1 className="text-2xl font-bold text-slate-900">Inventario de Local</h1>
        </div>
        {location && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>{location.name}</span>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Inventarios</span>
              <ClipboardList className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_inventories}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Hoy</span>
              <Calendar className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-600 mt-1">{stats.inventories_today}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4 col-span-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Último Inventario</span>
              <Clock className="w-5 h-5 text-blue-500" />
            </div>
            {stats.last_inventory ? (
              <p className="text-lg font-medium text-slate-700 mt-1">
                {stats.last_inventory.inventory_date} - {stats.last_inventory.inventory_time}
                <span className="text-sm text-muted-foreground ml-2">
                  ({stats.last_inventory.total_items} productos)
                </span>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">Sin inventarios</p>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('inventory')}
          className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'inventory'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-slate-900'
          }`}
        >
          <Plus className="w-4 h-4" />
          Nuevo Inventario
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'history'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-slate-900'
          }`}
        >
          <History className="w-4 h-4" />
          Historial
        </button>
      </div>

      {/* Tab: Nuevo Inventario */}
      {activeTab === 'inventory' && (
        <div className="space-y-6">
          {/* Selector de Sub-Bodega (prominente) */}
          {subWarehouses.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex items-center gap-2">
                  <Warehouse className="w-5 h-5 text-amber-600" />
                  <Label className="font-medium text-amber-800">Sub-Bodega:</Label>
                </div>
                <Select value={subWarehouseFilter} onValueChange={setSubWarehouseFilter}>
                  <SelectTrigger className="w-full sm:w-[280px] bg-white">
                    <SelectValue placeholder="Seleccionar sub-bodega" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">📦 Todos los productos (sin filtro)</SelectItem>
                    {subWarehouses.map(sw => (
                      <SelectItem key={sw.id} value={sw.id}>
                        🏷️ {sw.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSubWarehouse && (
                  <span className="text-sm text-amber-700">
                    Contando solo productos de: <strong>{selectedSubWarehouse.name}</strong>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Barra de acciones */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar producto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {uniqueSuppliers.length > 0 && (
                  <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                    <SelectTrigger className="w-full sm:w-[200px]">
                      <Filter className="w-4 h-4 mr-2 text-slate-400" />
                      <SelectValue placeholder="Filtrar proveedor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los proveedores</SelectItem>
                      {uniqueSuppliers.map(supplier => (
                        <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => exportToPDF()}
                  disabled={itemsWithQuantity.length === 0}
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Exportar PDF
                </Button>
                <Button onClick={saveInventory} disabled={saving || itemsWithQuantity.length === 0}>
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar Inventario
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Resumen actual */}
          {itemsWithQuantity.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">
                  {itemsWithQuantity.length} productos con {totalQuantity} unidades en total
                </span>
              </div>
            </div>
          )}

          {/* Tabla de productos */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Código</th>
                    <th className="px-4 py-3 text-left font-semibold">Producto</th>
                    <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
                    <th className="px-4 py-3 text-left font-semibold">Unidad</th>
                    <th className="px-4 py-3 text-center font-semibold w-32">Cantidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-sm">{item.code}</td>
                      <td className="px-4 py-3 font-medium">{item.name}</td>
                      <td className="px-4 py-3 text-muted-foreground text-sm">{item.supplier || '-'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{item.unit}</td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={inventoryItems[item.id] || ''}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          className="w-24 text-center mx-auto"
                          placeholder="0"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notas */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <Label className="text-sm font-medium mb-2 block">Notas (opcional)</Label>
            <Textarea
              placeholder="Observaciones del inventario..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Tab: Historial */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" onClick={loadHistory} disabled={historyLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : history.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-muted-foreground">No hay inventarios registrados</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                    <th className="px-4 py-3 text-left font-semibold">Hora</th>
                    <th className="px-4 py-3 text-left font-semibold">Local</th>
                    <th className="px-4 py-3 text-right font-semibold">Productos</th>
                    <th className="px-4 py-3 text-right font-semibold">Total Unidades</th>
                    <th className="px-4 py-3 text-left font-semibold">Creado por</th>
                    <th className="px-4 py-3 text-center font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">{inv.inventory_date}</td>
                      <td className="px-4 py-3 text-muted-foreground">{inv.inventory_time}</td>
                      <td className="px-4 py-3 font-medium">{inv.location_name}</td>
                      <td className="px-4 py-3 text-right">{inv.total_items}</td>
                      <td className="px-4 py-3 text-right font-medium">{inv.total_quantity}</td>
                      <td className="px-4 py-3 text-muted-foreground">{inv.created_by_name || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewInventoryDetails(inv.id)}
                          >
                            Ver
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => exportToPDF(inv)}
                            className="text-green-600"
                          >
                            <FileDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Dialog: Ver Inventario */}
      <Dialog open={viewInventoryDialog.open} onOpenChange={(open) => !open && setViewInventoryDialog({ open: false, inventory: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Inventario</DialogTitle>
          </DialogHeader>
          
          {viewInventoryDialog.inventory && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Local</p>
                  <p className="font-medium">{viewInventoryDialog.inventory.location_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fecha y Hora</p>
                  <p className="font-medium">
                    {viewInventoryDialog.inventory.inventory_date} {viewInventoryDialog.inventory.inventory_time}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Productos</p>
                  <p className="font-medium">{viewInventoryDialog.inventory.total_items}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Unidades</p>
                  <p className="font-medium">{viewInventoryDialog.inventory.total_quantity}</p>
                </div>
              </div>

              {viewInventoryDialog.inventory.notes && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Notas</p>
                  <p className="text-amber-800">{viewInventoryDialog.inventory.notes}</p>
                </div>
              )}

              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Código</th>
                    <th className="px-3 py-2 text-left">Producto</th>
                    <th className="px-3 py-2 text-right">Cantidad</th>
                    <th className="px-3 py-2 text-left">Unidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {viewInventoryDialog.inventory.items.map((item) => (
                    <tr key={item.item_id || item.item_code}>
                      <td className="px-3 py-2 font-mono">{item.item_code}</td>
                      <td className="px-3 py-2">{item.item_name}</td>
                      <td className="px-3 py-2 text-right font-medium">{item.quantity}</td>
                      <td className="px-3 py-2 text-muted-foreground">{item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => exportToPDF(viewInventoryDialog.inventory)}
            >
              <FileDown className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
            <Button onClick={() => setViewInventoryDialog({ open: false, inventory: null })}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventaristaPage;
